import { useEffect, useMemo, useCallback } from "react";
import DriverHomeBg from "@/assets/backgrounds/DriverHomeBg.svg";
import DriverFlowButtonCard from "@/components/custom/DriverFlowButtonCard";
import DriverProfileCard from "@/components/custom/DriverProfileCard";
import CheckInDriverProfileCard from "@/components/custom/CheckInDriverProfileCard";
import PageLayout from "@/components/custom/PageLayout";
import { cn, convertTo12HourFormat } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/redux/store";
import { fetchDriverDetails } from "@/redux/driverDetailsSlice";
import { FlowEnums } from "@/central-atoms/everest-fleet-pwa/enums";
import { useNavigate } from "react-router-dom";
import AllotmentCard from "@/components/custom/AllotmentCard";
import { clearAuth } from "@/redux/authSlice";
import { UserAdditionalProps } from "@/central-atoms/users/types";
import { initializeFeatureFlag, removeActiveFleetId } from "@/utils/appInitializer";
import { isNumber } from "lodash";

const DriverHome = () => {
  const driverDetails = useSelector(
    (state: RootState) => state.driverDetails.details?.data
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const refreshDriverDetails = useCallback(() => {
    dispatch(fetchDriverDetails());
  }, [dispatch]);

  useEffect(() => {
    refreshDriverDetails();

    // A PWA brought back from the background keeps showing whatever was rendered last, so the
    // supervisor's approval in between is invisible until the driver reloads. Refetch on resume.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshDriverDetails();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshDriverDetails]);

  const handleInitiateFlow = useCallback(
    (type?: FlowEnums) => {
      switch (type) {
        case FlowEnums.LOGIN:
          navigate("/driver-login-flow");
          break;
        case FlowEnums.CHECK_IN:
          navigate("/driver-check-in");
          break;
        case FlowEnums.SUBMIT:
          navigate("/driver-topup");
          break;
        case FlowEnums.ROSTER:
          navigate("/driver-roster");
          break;
        default:
          navigate("/driver-charge");
          break;
      }
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    dispatch(clearAuth());
    localStorage.removeItem("auth_token");
    removeActiveFleetId();
    navigate("/signin");
  }, [dispatch, navigate]);

  const {
    user,
    reporting_time: reportingTime,
    disable_check_in: disableCheckIn,
    allotment_details: allocationDetails,
    login_token: loginToken,
    check_in_token: checkInToken,
    status: allocationStatus,
    disable_login: disableLogin,
    disable_roster: disableRoster,
    check_in_success: checkInSuccess,
    topup,
  } = driverDetails || {};

  const userName = user?.name;
  const { external_user_props: externalUserProps } =
    (user?.additional_props as UserAdditionalProps) || {};
  const hub = externalUserProps?.driver_hub;
  const driverId = externalUserProps?.driver_id;

  const renderDriverCards = useMemo(() => {
    if (checkInToken) {
      return (
        <CheckInDriverProfileCard
          handleLogout={handleLogout}
          checkInToken={checkInToken || ""}
          name={userName || ""}
          hub={hub || ""}
          driverId={driverId || ""}
        />
      );
    } else if (!checkInSuccess && Boolean(allocationDetails?.car_details)) {
      return (
        <div className="mb-8">
          <DriverProfileCard
            handleLogout={handleLogout}
            statusType={disableLogin ? "danger" : "safe"}
            name={userName || ""}
            hub={hub || ""}
            driverId={driverId || ""}
            entryType={loginToken ? "login_token" : "report_at"}
            entryTypeText={
              loginToken || convertTo12HourFormat(reportingTime || "")
            }
            driverStatus={allocationStatus || ""}
          />
          <AllotmentCard
            vrn={allocationDetails?.car_details.vrn || ""}
            soc={allocationDetails?.car_details.soc || 0}
            odo={allocationDetails?.car_details.odo || 0}
            unitConsumption={parseFloat(
              (allocationDetails?.units_consumed || 0).toFixed(2)
            )}
            totalFastChargingSessions={
              allocationDetails?.charging_sessions || 0
            }
            allowedCycles={isNumber(allocationDetails?.allowed_cycles) ? allocationDetails?.allowed_cycles : null}
          />
        </div>
      );
    } else {
      return (
        <DriverProfileCard
          handleLogout={handleLogout}
          statusType="safe"
          name={userName || ""}
          hub={hub || ""}
          driverId={driverId || ""}
          entryType={loginToken ? "login_token" : "report_at"}
          entryTypeText={
            loginToken || convertTo12HourFormat(reportingTime || "")
          }
          driverStatus={allocationStatus || ""}
        />
      );
    }
  }, [
    checkInToken,
    allocationDetails,
    disableLogin,
    userName,
    hub,
    driverId,
    loginToken,
    reportingTime,
    allocationStatus,
    checkInSuccess,
    handleLogout,
  ]);

  return (
    <div className="relative w-full">
      <div className="absolute w-full">
        <img
          src={DriverHomeBg}
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>
      <PageLayout>
        <div className="h-full relative">
          {renderDriverCards} {/* Top Card */}
          <DriverFlowButtonCard
            disableLogin={disableLogin as boolean}
            disableCheckIn={disableCheckIn as boolean}
            disableRoster={disableRoster ?? true}
            showTopup={topup?.enabled === true}
            handleFlowStart={handleInitiateFlow}
          />
          {/* Button Container */}
          {/* <Button
            disabled={!isCarAlloted}
            variant="secondary"
            className={cn(
              "px-4 py-2 h-[47px] text-[14px] rounded-[10px]",
              !isCarAlloted ? "bg-[#BABABA] text-white" : ""
            )}
            onClick={() => handleInitiateFlow()}
          >
            <img src={ChargeFlowIcon} alt="charge icon" className="mr-[8px]" />
            {i18n.t("charge")}
          </Button> */}
        </div>
      </PageLayout>
    </div>
  );
};

export default DriverHome;
